import { z } from 'zod';

/**
 * Supported provider identifiers. Selected at runtime via `env.AI_PROVIDER`
 * or explicitly through {@link createAiClient}.
 */
export const AI_PROVIDERS = ['anthropic', 'openai', 'deepseek'] as const;

export type AiProviderName = (typeof AI_PROVIDERS)[number];

/**
 * Role of a single message in a chat conversation.
 *
 * - `system`   high-level instructions that steer the assistant.
 * - `user`     input from the end user.
 * - `assistant` a prior model response (for multi-turn context).
 */
export type ChatRole = 'system' | 'user' | 'assistant' | 'tool';

/** A function the model may call. `parameters` is a JSON Schema for the arguments. */
export interface ToolDefinition {
  readonly name: string;
  readonly description?: string;
  readonly parameters: Record<string, unknown>;
}

/** A tool invocation emitted by the model. `arguments` is a raw JSON string. */
export interface ToolCall {
  readonly id: string;
  readonly name: string;
  readonly arguments: string;
}

/** How the model may use tools: pick freely, never, or must call one. */
export type ToolChoice = 'auto' | 'none' | 'required';

/** A single turn in a chat conversation. */
export interface ChatMessage {
  readonly role: ChatRole;
  /** Visible text. Empty is allowed for an assistant turn that only emits `toolCalls`. */
  readonly content: string;
  /** For `role: 'tool'` — the id of the tool call this message answers. */
  readonly toolCallId?: string;
  /** For `role: 'assistant'` — the tool calls the model emitted on this turn. */
  readonly toolCalls?: readonly ToolCall[];
}

/**
 * A canonical, provider-agnostic chat request. Providers translate this into
 * their own wire formats (Anthropic Messages API, OpenAI Chat Completions, ...).
 */
export interface ChatRequest {
  /**
   * Model identifier. When omitted, the provider falls back to its configured
   * default (e.g. `claude-opus-4-8` for Anthropic, `gpt-4o` for OpenAI).
   */
  readonly model?: string;
  /** Ordered conversation history. Must contain at least one non-system turn. */
  readonly messages: readonly ChatMessage[];
  /**
   * Sampling temperature in the `[0, 2]` range. Higher values produce more
   * varied output. Providers that do not accept sampling params ignore this.
   */
  readonly temperature?: number;
  /** Upper bound on tokens to generate. */
  readonly maxTokens?: number;
  /** Optional top-level system prompt (merged with any `system` messages). */
  readonly system?: string;
  /**
   * When `true`, callers are expected to consume the result via
   * {@link AiProvider.stream}. The flag is also forwarded to providers so they
   * can opt into server-side streaming transports.
   */
  readonly stream?: boolean;
  /** Optional per-request abort signal. */
  readonly signal?: AbortSignal;
  /** Arbitrary provider-specific metadata (forwarded verbatim where supported). */
  readonly metadata?: Readonly<Record<string, string>>;
  /**
   * Provider-agnostic reasoning / "thinking" control. Providers that expose an
   * extended-thinking mode (e.g. DeepSeek v4) honor this; others ignore it.
   *
   * - `true`           enable thinking at the provider default effort.
   * - `false`          explicitly disable thinking (force a fast answer).
   * - `{ effort }`     enable thinking at the given effort level.
   * - omitted          leave the provider's own default in place.
   */
  readonly reasoning?: boolean | { readonly effort?: ReasoningEffort };
  /** Tools (functions) the model may call. Honored by tool-capable providers (DeepSeek). */
  readonly tools?: readonly ToolDefinition[];
  /** Tool-use policy. Defaults to the provider's behavior ('auto') when tools are present. */
  readonly toolChoice?: ToolChoice;
  /**
   * Output-format constraint. `'json_object'` maps to DeepSeek's
   * `response_format: {type: 'json_object'}` (valid-JSON output). Caveats per
   * docs/futurex/review.md: the prompt MUST contain the literal word "json"
   * (+ ideally an example), and the SHAPE is not guaranteed — validate with
   * Zod and run a bounded repair round (see `callJson` in
   * `@fim/futurex-context-engine`). Ignored by providers without support.
   */
  readonly responseFormat?: 'json_object';
}

/** Reasoning effort levels recognised by thinking-capable providers. */
export type ReasoningEffort = 'high' | 'max';

/**
 * One incremental piece of a streamed completion.
 *
 * `delta` carries the newly generated text since the previous chunk (empty on
 * the terminal chunk). `done` is `true` exactly once, on the final chunk.
 */
export interface ChatChunk {
  readonly delta: string;
  readonly done: boolean;
  /**
   * Incremental chain-of-thought text from a thinking-capable provider, emitted
   * before the visible answer. Empty/absent for providers without a reasoning
   * channel or when thinking is disabled.
   */
  readonly reasoningDelta?: string;
  /**
   * Incremental tool-call fragment. Tool-call arguments stream as JSON fragments
   * across chunks and must be reassembled by `index` — the provider also emits the
   * assembled {@link ChatChunk.toolCalls} on the terminal chunk.
   */
  readonly toolCallDelta?: {
    readonly index: number;
    readonly id?: string;
    readonly name?: string;
    readonly argumentsDelta?: string;
  };
  /** Populated on the terminal chunk when the model emitted tool calls. */
  readonly toolCalls?: readonly ToolCall[];
  /** Populated on the terminal chunk when the provider reports a stop reason. */
  readonly finishReason?: AiFinishReason;
  /** Populated on the terminal chunk when usage accounting is available. */
  readonly usage?: AiUsage;
}

export type AiFinishReason =
  | 'stop'
  | 'length'
  | 'content_filter'
  | 'tool_use'
  | 'error'
  | 'unknown';

/** Token accounting returned by providers that expose it. */
export interface AiUsage {
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly totalTokens: number;
  /**
   * Prefix-cache split, for providers with automatic caching (DeepSeek). The
   * cache-hit portion of input is billed at a fraction (~1/50th) of cache-miss,
   * so this is the lever the cost model + cache-ratio metric depend on. Absent
   * when the provider does not report it.
   */
  readonly cacheHitTokens?: number;
  readonly cacheMissTokens?: number;
  /**
   * Tokens spent on chain-of-thought ("thinking"), when the provider meters them
   * separately (DeepSeek `completion_tokens_details.reasoning_tokens`). A subset
   * of `outputTokens`, surfaced so cost accounting can attribute reasoning spend.
   * Absent when thinking is off or the provider does not report it.
   */
  readonly reasoningTokens?: number;
}

/** A full, non-streamed chat completion result. */
export interface ChatResult {
  readonly content: string;
  readonly model: string;
  readonly finishReason: AiFinishReason;
  /** Tool calls the model emitted, if any (finishReason will be 'tool_use'). */
  readonly toolCalls?: readonly ToolCall[];
  readonly usage?: AiUsage;
  /**
   * The full chain-of-thought returned by a thinking-capable provider, when
   * thinking was enabled. Absent otherwise.
   */
  readonly reasoning?: string;
}

/** Request to embed one or more text inputs into dense vectors. */
export interface EmbedRequest {
  /** Embedding model id (provider default used when omitted). */
  readonly model?: string;
  /** One or more strings to embed. */
  readonly input: readonly string[];
  /** Optional output dimensionality, where the provider supports it. */
  readonly dimensions?: number;
  readonly signal?: AbortSignal;
}

/** Result of an {@link EmbedRequest}: one vector per input, in order. */
export interface EmbedResponse {
  readonly model: string;
  readonly embeddings: readonly (readonly number[])[];
  readonly usage?: Pick<AiUsage, 'inputTokens' | 'totalTokens'>;
}

/** Lightweight description of a model the SDK knows how to address. */
export interface AiModel {
  readonly id: string;
  readonly provider: AiProviderName;
  readonly contextWindow: number;
  readonly maxOutputTokens: number;
  readonly supportsStreaming: boolean;
  readonly supportsEmbeddings: boolean;
}

/* -------------------------------------------------------------------------- */
/* Runtime validation schemas (Zod)                                           */
/* -------------------------------------------------------------------------- */

export const chatRoleSchema = z.enum(['system', 'user', 'assistant', 'tool']);

export const toolCallSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  arguments: z.string(),
});

export const toolDefinitionSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  parameters: z.record(z.unknown()),
});

export const chatMessageSchema = z.object({
  role: chatRoleSchema,
  // Empty content is valid for an assistant turn that only carries tool_calls.
  content: z.string(),
  toolCallId: z.string().optional(),
  toolCalls: z.array(toolCallSchema).optional(),
});

export const chatRequestSchema = z.object({
  model: z.string().min(1).optional(),
  messages: z.array(chatMessageSchema).min(1, 'at least one message is required'),
  temperature: z.number().min(0).max(2).optional(),
  maxTokens: z.number().int().positive().optional(),
  system: z.string().optional(),
  stream: z.boolean().optional(),
  metadata: z.record(z.string()).optional(),
  reasoning: z
    .union([z.boolean(), z.object({ effort: z.enum(['high', 'max']).optional() })])
    .optional(),
  tools: z.array(toolDefinitionSchema).optional(),
  toolChoice: z.enum(['auto', 'none', 'required']).optional(),
  responseFormat: z.literal('json_object').optional(),
});

export const embedRequestSchema = z.object({
  model: z.string().min(1).optional(),
  input: z.array(z.string().min(1)).min(1, 'at least one input is required'),
  dimensions: z.number().int().positive().optional(),
});

export type ChatRequestInput = z.infer<typeof chatRequestSchema>;
export type EmbedRequestInput = z.infer<typeof embedRequestSchema>;
